import { useEffect, useState } from 'react';

import ModalWrap from '../../common/modal-wrap';
import Button from '../../common/button';
import { CloseCrossBtn } from '../../icons/close-cross-btn';

import { useFormFactor } from '../../../utils/formFactor';

import { ILifeItem } from './types';
import { WheelOfBalance } from './WheelOfBalance';

export interface IWheelPanel {
  isClient?: boolean;
  withTips?: boolean;
  small?: boolean;
  inJournal?: boolean;
  maxModalWidth?: number;
  values: number[];
  setValues?: (ar: number[]) => void;
  setReady?: () => void;
  onButtonTips?: () => void;
  showClientSelectingOverlay?: boolean;
}

function getContrastColorForHex(hex: string) {
  const c = hex.substring(1);
  const rgb = parseInt(c, 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = rgb & 0xff;
  const luma = 0.299 * r + 0.587 * g + 0.114 * b;
  return luma > 186 ? '#000' : '#fff';
}

export const WheelPanel = ({
  isClient = false,
  withTips = false,
  small = false,
  inJournal = false,
  maxModalWidth = 960,
  values,
  setValues,
  setReady,
  onButtonTips,
  showClientSelectingOverlay = false,
}: IWheelPanel) => {
  const lifeItems: ILifeItem[] = [
    { color: '#A9A9F5', title: 'Семья, родные', shortTitle: 'Семья' },
    { color: '#C173C1', title: 'Здоровье', shortTitle: 'Здоровье' },
    { color: '#7FD1D5', title: 'Друзья,  отношения с другими', shortTitle: 'Друзья' },
    { color: '#3B4CC0', title: 'Личностный рост, духовное развитие', shortTitle: 'Развитие' },
    { color: '#E60000', title: 'Карьера, бизнес, профессиональный рост', shortTitle: 'Карьера' },
    { color: '#F2EA00', title: 'Финансы, инвестиции, деньги', shortTitle: 'Финансы' },
    { color: '#2CA02C', title: 'Отдых, хобби, путешествие', shortTitle: 'Отдых' },
    { color: '#CFA401', title: 'Физическое окружение, материальные ценности', shortTitle: 'Имущество' },
  ];
  const [modalShow, setModalShow] = useState(true);
  const [editMode, setEditMode] = useState(isClient && setValues !== undefined);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const formFactor = useFormFactor();
  const shouldShowClientOverlay = Boolean(showClientSelectingOverlay && !isClient && !inJournal);

  const renderClientSelectingOverlay = () =>
    shouldShowClientOverlay ? (
      <div className="absolute inset-0 z-30 pointer-events-none flex items-center justify-center">
        <div
          className="px-2 py-2 rounded-[10px] bg-[#6B7280]/60 text-white text-center font-comfortaa font-semibold text-xs md:text-sm uppercase tracking-wide shadow-lg animate-pulse"
          style={{ animationDuration: '5s', transform: 'translateY(-20px)' }}
        >
          Клиент выбирает
        </div>
      </div>
    ) : null;

  useEffect(() => {
    if (setValues && isClient) {
      setEditMode(true);
    }
  }, [isClient, setValues]);

  const onReady = () => {
    if (setReady && isClient) {
      setReady();
    }
    setEditMode(false);
  };

  const onChange = (result: number[]) => {
    if (setValues && editMode) {
      setValues(result);
    }
  };

  const mainText = '!text-[18px]';
  const textOnSmall = 'text-xs md:text-sm lg:text-base xl:text-lg 2xl:text-xl largePC:text-2xl';
  const textAtJournal = 'font-medium text-blackBase';

  const rightItems = lifeItems.slice(0, 4);
  const leftItems = lifeItems.slice(-4).reverse();

  const modal = (
    <ModalWrap isShow={modalShow} className="bg-greyBase40">
      <div
        // eslint-disable-next-line max-len
        className={`max-w-full z-[10] w-[${maxModalWidth}px] lg:min-w-[${maxModalWidth}px] bg-[#262626]  rounded-[20px] sm:rounded-[40px] border-[1.53px] border-color-[#404040] p-[2rem_2rem_1rem_2rem] font-comfortaa`}
      >
        <div className="relative text-[1.2rem] flex gap-[1.2vw] justify-between items-center mb-[6vw] lg:mb-[2vw]">
          <h2 className="font-bold text-white">Колесо жизненного баланса</h2>
          <div
            className="absolute top-0 right-0 lg:top-[5px] lg:right-[5px] cursor-pointer"
            onClick={() => setModalShow(false)}
          >
            <CloseCrossBtn fill="white" fillBg="red" className="hover:cursor-pointer" />
          </div>
        </div>
        <p className="font-normal text-[1rem] text-[#D4D4D4] mb-[30px] lg:mb-[40px]">
          Оцените каждую сферу вашей жизни по шкале от 1 до 10, где:
        </p>
        <div className="ml-[1.1vw] pb-[1rem] flex flex-col font-normal text-[1rem] text-[#D4D4D4] mr-[-1.1vw] ">
          <div>1 - полная неудовлетворенность</div>
          <div>10 - полная удовлетворенность</div>
        </div>
        <div className="ml-[1.1vw] pb-[1rem] flex flex-col font-normal text-[1rem] text-[#D4D4D4] mr-[-1.1vw] ">
          <div className="mt-[1vw]">Нажмите на сегмент, чтобы изменить оценку.</div>
        </div>

        <div className="flex justify-end">
          <Button type={'grey'} className="w-max " onClick={() => setModalShow(false)}>
            <div className={'font-bold text-sm'}>Понятно</div>
          </Button>
        </div>
      </div>
    </ModalWrap>
  );

  const titleSize = small ? mainText : 'text-lg md:text-lx lg:text-2lx xl:text-3xl 2xl:text-4xl largePC:text-5xl';
  const numberText = inJournal
    ? 'text-xs/2 md:text-sm/2 lg:text-base'
    : small
      ? 'text-xs/2 md:text-sm/2 lg:text-base xl:text-lg 2xl:text-xl largePC:text-2xl'
      : mainText;

  return (
    <div
      className={'relative h-full w-full flex flex-col m-0.5 rounded-[10px] bg-[#F6F6F6] items-center overflow-hidden'}
    >
      <button
        type="button"
        onClick={() => setIsFullscreen(true)}
        className={`absolute flex items-center gap-1 z-20 bg-black/60 text-white text-xs md:text-sm px-1 py-1 rounded hover:bg-black/70 ${
          formFactor === 'mobile-portrait' ? 'bottom-2 left-2' : 'top-2 left-2'
        }`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zm-3-12v2h3v3h2V5h-5z" />
        </svg>
        Увеличить
      </button>
      {!inJournal && (
        <div
          className={`flex-none font-comfortaa font-bold text-blackBase ${titleSize} `}
          style={{ cursor: 'default' }}
        >
          Колесо жизненного баланса
        </div>
      )}
      <div className={'relative flex flex-row w-full flex-nowrap flex-1 min-h-0 justify-between overflow-auto'}>
        {inJournal ? (
          <div className={`w-full h-full p-2 ${formFactor === 'mobile-portrait' ? 'pb-8' : 'pt-12'}`}>
            <div className="grid grid-cols-2 gap-3 font-comfortaa font-bold text-[#111026]">
              {lifeItems.map((item, idx) => {
                const bg = item.color;
                const fg = getContrastColorForHex(bg);
                const val = values[idx] === 0 ? '' : String(values[idx]);
                return (
                  <div key={`journal-title-${idx}`} className="flex items-center gap-2">
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '24px',
                        height: '24px',
                        minWidth: '24px',
                        minHeight: '24px',
                        borderRadius: '50%',
                        backgroundColor: bg,
                        color: fg,
                        fontSize: '14px',
                        fontWeight: 700,
                        lineHeight: '24px',
                        textAlign: 'center',
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.1) inset',
                      }}
                    >
                      {val}
                    </span>
                    <span>{item.title}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className={`flex grow-3 min-h-0 font-comfortaa font-bold ${numberText} text-[#111026] pt-[0.2rem]`}>
            <div className="relative w-full h-full flex items-center justify-center">
              <WheelOfBalance
                lifeItems={lifeItems}
                values={values}
                saveResult={editMode ? onChange : undefined}
                disableHover
              />
              {renderClientSelectingOverlay()}
            </div>
          </div>
        )}
      </div>

      {editMode && (
        <Button
          type={'primary'}
          className={'!w-auto min-w-[100px] !text-xs xl:!text-base mt-[0.5rem] mb-[0.5rem]'}
          onClick={() => onReady()}
        >
          Готово
        </Button>
      )}
      {!isClient &&
        onButtonTips !== undefined && ( //!self-center  !items-end` !justify-center
          <Button
            type={'primary'}
            className={'!w-auto p-[6px_10px] min-w-[200px] !text-sm xl:!text-base mt-[1rem] mb-[1rem]'}
            onClick={() => onButtonTips()}
          >
            {withTips ? 'Закрыть подсказки' : 'Подсказки'}
          </Button>
        )}
      {isClient && modal}
      {isFullscreen && (
        <div className="fixed inset-0 z-[1000] bg-black/80">
          <div className="flex items-center justify-between p-2">
            <div className="flex-1 flex justify-start">
              <button
                type="button"
                onClick={() => setIsFullscreen(false)}
                className={
                  'flex items-center gap-1 bg-black/60 text-white text-xs md:text-sm px-1 py-1 rounded hover:bg-black/70'
                }
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 9H5V5h4v2H7v2h2v2zm6-2h-2V5h4v4h-2V7zm-6 6H7v-2H5v4h4v-2zm6 0v2h2v2h-4v-4h2z" />
                </svg>
                Уменьшить
              </button>
            </div>
            <div className="flex-1 flex justify-end">
              {editMode && (
                <Button
                  type={'primary'}
                  className={'!w-auto min-w-[100px] !text-xs xl:!text-base'}
                  onClick={() => {
                    onReady();
                    setIsFullscreen(false);
                  }}
                >
                  Готово
                </Button>
              )}
            </div>
          </div>
          <div className="w-full h-full flex items-center justify-center max-h-[90vh]">
            <div className="w-full h-full bg-white rounded-[20px]  p-1">
              <div className="relative w-full h-full flex items-center justify-center">
                <WheelOfBalance
                  lifeItems={lifeItems}
                  values={values}
                  fullscreen={true}
                  saveResult={editMode ? onChange : undefined}
                  disableHover
                />
                {renderClientSelectingOverlay()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
